import * as React from 'react';
import { useEffect, useRef, useState, ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import { Switch } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import JSONEditor from 'jsoneditor';
import {enqueueSnackbar} from "notistack";
import 'jsoneditor/dist/jsoneditor.min.css';

interface JsonEditorProps {
  json: any;
  schema?: any;
  onChangeJson?: (data: any) => void;
}

export function JsonEditor({ json, schema, onChangeJson }: JsonEditorProps): React.JSX.Element {
  const [mode, setMode] = useState<'tree' | 'code'>('tree');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<JSONEditor | null>(null);

  useEffect(() => {
    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (containerRef.current != null) {
      if (editorRef.current != null) {
        if (JSON.stringify(editorRef.current?.get()) !== JSON.stringify(json)) {
          editorRef.current.set(json);
        }
        if (schema) {
          editorRef.current.setSchema(schema);
        }
      } else {
        editorRef.current = new JSONEditor(containerRef.current, {
          schema,
          onChange: () => {
            const jsonData = editorRef.current?.get();
            onChangeJson?.(jsonData);
          },
        });
        editorRef.current.set({...json});
      }
    }
  }, [containerRef, json, schema, onChangeJson]);

  useEffect(() => {
    if (containerRef.current != null) {
      if (editorRef.current != null) {
        try {
          editorRef.current.setMode(mode);
        } catch (e) {
          setMode('code');
          enqueueSnackbar(e as string, { variant: 'error' });
        }
      }
    }
  }, [containerRef, mode]);

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            checked={mode === 'code'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMode(e.target.checked ? 'code' : 'tree')}
            name="textMode"
          />
        }
        label="Text mode"
      />
      <Box sx={{ height: '500px', overflow: 'visible' }} className="jsoneditor-react-container" ref={containerRef} />
    </>
  );
}
